import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useComplaintConfig, useSaveComplaintConfig } from "../../lib/api";
import { categoryLabel } from "../../lib/complaints";
import {
  DetailHeader,
  FieldLabel,
  GhostButton,
  PrimaryButton,
  SecondaryButton,
  inputClass,
} from "./primitives";

const NUMBER_FIELDS = [
  ["auto_close_days", "Auto-close resolved after", "days", 1, 60],
  ["reopen_window_days", "Reopen window", "days", 0, 90],
  ["dedupe_window_minutes", "Merge duplicates within", "minutes", 0, 60],
  ["max_listed_complaints", "Open complaints sent to AI", "max", 1, 25],
];

function toCategoryKey(text) {
  return text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const toForm = (data) => (data ? { ...data, categories: [...(data.categories || [])] } : null);

export default function ComplaintConfigEditor({ tenantId, title }) {
  const { data, isLoading } = useComplaintConfig(tenantId);
  const save = useSaveComplaintConfig(tenantId);
  const baseline = useMemo(() => toForm(data), [data]);
  const [form, setForm] = useState(null);
  const [draft, setDraft] = useState("");

  useEffect(() => setForm(baseline), [baseline]);

  if (isLoading || !form) {
    return (
      <div className="flex items-center gap-2 text-[13px] text-[#6B7280]">
        <Loader2 className="animate-spin" size={15} /> Loading
      </div>
    );
  }

  const dirty = JSON.stringify(form) !== JSON.stringify(baseline);

  const addCategory = () => {
    const key = toCategoryKey(draft);
    if (!key) return;
    setForm((f) =>
      f.categories.includes(key) ? f : { ...f, categories: [...f.categories.filter((c) => c !== "other"), key, "other"] },
    );
    setDraft("");
  };

  const onSave = async () => {
    try {
      await save.mutateAsync({
        enabled: form.enabled,
        categories: form.categories,
        ...Object.fromEntries(NUMBER_FIELDS.map(([key]) => [key, Number(form[key])])),
      });
      toast.success("Complaint tracking saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  return (
    <>
      <DetailHeader
        title={title}
        toggleLabel="Track complaints"
        status={form.enabled !== baseline.enabled ? "Not saved" : ""}
        checked={form.enabled}
        onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
      />
      <div className="flex max-w-[640px] flex-col gap-6">
        <div>
          <FieldLabel>Categories</FieldLabel>
          <div className="flex flex-wrap gap-2">
            {form.categories.map((key) => (
              <span
                key={key}
                className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[#EFF5FC] pl-3 pr-2 text-[13px] font-medium text-[#1F5596]"
              >
                {categoryLabel(key)}
                {key !== "other" && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, categories: f.categories.filter((c) => c !== key) }))}
                    aria-label={`Remove ${categoryLabel(key)}`}
                    className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-full text-[#2667B5] hover:bg-[#DCE9F8]"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </span>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCategory();
                }
              }}
              placeholder="New category"
              aria-label="New category name"
              className={`${inputClass} !w-60 max-w-full`}
            />
            <SecondaryButton onClick={addCategory}>Add</SecondaryButton>
          </div>
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          {NUMBER_FIELDS.map(([key, label, unit, min, max]) => (
            <div key={key}>
              <label htmlFor={`complaint-config-${key}`} className="mb-[6px] block text-[13px] font-medium text-[#374151]">
                {label}
              </label>
              <div className="flex items-center gap-[10px]">
                <input
                  id={`complaint-config-${key}`}
                  type="number"
                  min={min}
                  max={max}
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  className={`${inputClass} !w-[120px]`}
                />
                <span className="text-[13px] text-[#6B7280]">{unit}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <PrimaryButton onClick={onSave} disabled={save.isPending}>
            {save.isPending && <Loader2 className="animate-spin" size={14} />}
            Save changes
          </PrimaryButton>
          {dirty && <GhostButton onClick={() => setForm(baseline)}>Discard</GhostButton>}
        </div>
      </div>
    </>
  );
}
